import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AthletesListParams } from "../../../admin-api/athletes";
import type { RemovalRequestsListParams } from "../../../admin-api/removals";
import { adminApi } from "../../../api-client";
import type { AthleteRemovalConfirmation } from "../../../schemas/athlete";
import { queryKeys } from "../../query-keys";

export function useAthletes(params: AthletesListParams = {}, enabled = true) {
	return useQuery({
		enabled,
		placeholderData: keepPreviousData,
		queryFn: () => adminApi.athletes.list(params),
		queryKey: queryKeys.athletes.list(params),
	});
}

export function useRemovalRequests(params: RemovalRequestsListParams) {
	return useQuery({
		queryFn: () => adminApi.athletes.removals.list(params),
		queryKey: queryKeys.athletes.removalList(params),
	});
}

export function useReviewRemoval() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
			adminApi.athletes.removals[action](id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.athletes.all }),
	});
}

export function useSendAthleteRemovalConfirmation() {
	return useMutation({
		mutationFn: ({ id, email }: AthleteRemovalConfirmation & { id: string }) =>
			adminApi.athletes.sendRemovalConfirmation(id, { email }),
	});
}

export function useAthlete(id: string) {
	return useQuery({
		enabled: Boolean(id),
		queryFn: () => adminApi.athletes.get(id),
		queryKey: queryKeys.athletes.detail(id),
	});
}

// The slug-suffixed athletes discovery says are the same person as their base
// row. Reading is free; merging deletes rows, so it stays a separate call.
export function useAthleteDuplicates() {
	return useQuery({
		queryFn: () => adminApi.athletes.duplicates(),
		queryKey: queryKeys.athletes.duplicates,
	});
}

export function useMergeAthleteDuplicates() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => adminApi.athletes.mergeDuplicates(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.athletes.all });
		},
	});
}

export function useSyncAthletes() {
	return useMutation({
		mutationFn: () => adminApi.athletes.sync(),
	});
}

export function useMigrateImages() {
	return useMutation({
		mutationFn: () => adminApi.athletes.migrateImages(),
	});
}

export function useEnrichAthleteImages() {
	return useMutation({
		mutationFn: () => adminApi.athletes.enrichImages(),
	});
}

export function useReplaceAthleteImage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ file, id }: { file: File; id: string }) =>
			adminApi.athletes.replaceImage(id, file),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.athletes.all,
			});
		},
	});
}

export function useCreateAthleteImage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ handle, id }: { handle?: string; id: string }) =>
			adminApi.athletes.createImage(id, { instagram_handle: handle }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.athletes.all,
			});
		},
	});
}

// The claims page has no pager, so ask for the server cap (100) instead of
// letting the default limit (20) silently drop rows past the first page.
const CLAIMS_PAGE_LIMIT = 100;

export function useClaimRequests(status?: string, userId?: string, offset = 0) {
	return useQuery({
		queryFn: () =>
			adminApi.athletes.claims.list({ limit: CLAIMS_PAGE_LIMIT, offset, status, user_id: userId }),
		queryKey: [...queryKeys.athletes.claims, status ?? "all", userId ?? "all", offset],
	});
}

export function useApprovedClaimsByUser(userId: string) {
	return useQuery({
		queryFn: async () => {
			const first = await adminApi.athletes.claims.list({
				limit: CLAIMS_PAGE_LIMIT,
				status: "approved",
				user_id: userId,
			});
			if (first.limit <= 0) throw new Error("Invalid claims page size");
			const claims = [...first.items];
			for (let offset = first.limit; offset < first.total; offset += first.limit) {
				const page = await adminApi.athletes.claims.list({
					limit: CLAIMS_PAGE_LIMIT,
					offset,
					status: "approved",
					user_id: userId,
				});
				claims.push(...page.items);
			}
			return claims;
		},
		queryKey: [...queryKeys.athletes.claims, "approved-by-user", userId],
	});
}

export function useCreateClaim() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			athleteId,
			userId,
			sendEmail,
		}: {
			athleteId: string;
			userId: string;
			sendEmail: boolean;
		}) => adminApi.athletes.claims.create(athleteId, { send_email: sendEmail, user_id: userId }),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.athletes.all }),
	});
}

export function useApproveClaim() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, sendEmail }: { id: string; sendEmail?: boolean }) =>
			adminApi.athletes.claims.approve(id, sendEmail),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.athletes.claims,
			});
		},
	});
}

export function useRejectClaim() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id }: { id: string }) => adminApi.athletes.claims.reject(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.athletes.claims,
			});
		},
	});
}

export function useRevokeClaim() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id }: { id: string }) => adminApi.athletes.claims.revoke(id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.athletes.all }),
	});
}

export function useSendClaimEmail() {
	return useMutation({
		mutationFn: ({ id }: { id: string }) => adminApi.athletes.claims.sendEmail(id),
	});
}

export function useScraperSources() {
	return useQuery({
		queryFn: () => adminApi.scraper.listSources(),
		queryKey: queryKeys.scraper.sources,
	});
}

export function useScrapeRuns() {
	return useQuery({
		queryFn: () => adminApi.scraper.listRuns(),
		queryKey: queryKeys.scraper.runs,
	});
}

export function useTriggerScrape() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ source }: { source: string }) => adminApi.scraper.triggerRun(source),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.scraper.runs,
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.scraper.sources,
			});
		},
	});
}

export function useRankingSyncs() {
	return useQuery({
		queryFn: () => adminApi.scraper.listSyncs(),
		queryKey: queryKeys.scraper.syncs,
	});
}
